import * as React from 'react';

import Alert from 'antd/lib/alert';
import Space from 'antd/lib/space';
import { QrcodeOutlined } from '@ant-design/icons';

import { LabelPaper } from '@app/objects/LabelPaper';
import { useTrackSize } from '@app/hooks/useTrackSize';

import {
	Legend,
	LegendFields,
} from '@app/components/Pages/Admin/LabelPaper/LabelPaperEditor/LabelPaperEditorComponents/InnerFormSections/Shared';

import Logo from '@images/logo.png';

import '@app/scss/ui/paper.scss';

export interface LabelData {
	id: string;

	pet: string;
	owner: string;

	clinic?: string;
	phone?: string;
}

export interface PaperPreviewProps {
	paper: LabelPaper;
	data: Array<LabelData>;

	offset?: number;
	printQr?: boolean;
	printIcon?: boolean;
	count?: number;
}

interface LabelPreviewProps {
	label: LabelMeasurements;
	item: LabelData;

	withQr?: boolean;
	withIcon?: boolean;
}

interface Size {
	width: number;
	height: number;
}

interface Margins {
	top: number;
	left: number;
	bottom: number;
	right: number;
}

interface Spacings {
	column: number;
	row: number;
}

interface LabelMeasurements {
	paddings: Margins;
	font: FontSizes;
	iconHeight: number;
}

interface FontSizes {
	title: number;
	subtitle: number;
	text: number;
}

/**
 * These are display measurements for rendered component
 * I.e. LabelPaper transformed for preview
 */
interface Measurements {
	size: Size;
	margins: Margins;
	spacings: Spacings;
	label: LabelMeasurements;
}

const defaultItem: Measurements = {
	size: {
		height: 0,
		width: 0,
	},
	margins: {
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
	},
	spacings: {
		column: 0,
		row: 0,
	},
	label: {
		paddings: {
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
		},
		font: {
			title: 0,
			subtitle: 0,
			text: 0,
		},
		iconHeight: 0,
	},
};

function getMeasurements(paper: LabelPaper, element: HTMLDivElement | null): Measurements {
	if (!element) return defaultItem;

	const root = {
		size: {
			width: element.clientWidth,
			height: element.clientHeight,
		},
		margins: {
			top: element.clientHeight * (paper.margins.top / paper.height),
			right: element.clientWidth * (paper.margins.right / paper.width),
			bottom: element.clientHeight * (paper.margins.bottom / paper.height),
			left: element.clientWidth * (paper.margins.left / paper.width),
		},
		spacings: {
			column: element.clientWidth * (paper.spacing.column / paper.width),
			row: element.clientHeight * (paper.spacing.row / paper.height),
		},
	};
	const label = {
		paddings: {
			top: element.clientHeight * (paper.labelPaddings.top / paper.height),
			right: element.clientWidth * (paper.labelPaddings.right / paper.width),
			bottom: element.clientHeight * (paper.labelPaddings.bottom / paper.height),
			left: element.clientWidth * (paper.labelPaddings.left / paper.width),
		},
	};

	const containerHeight = (root.size.height - root.margins.top - root.margins.bottom);
	const containerWidth = (root.size.width - root.margins.left - root.margins.right);
	const rowHeight = (containerHeight / paper.rows) - root.spacings.row * (paper.rows - 1);
	const columnWidth = (containerWidth / paper.columns) - root.spacings.column * (paper.columns - 1);
	const labelHeight = rowHeight - label.paddings.top - label.paddings.bottom;
	const labelWidth = columnWidth - label.paddings.left - label.paddings.right;
	const baseline = labelHeight / 100;

	return {
		size: root.size,
		margins: root.margins,
		spacings: root.spacings,
		label: {
			paddings: label.paddings,
			font: {
				title: baseline * paper.typography.titleFontSize,
				subtitle: baseline * paper.typography.headerFontSize,
				text: baseline * paper.typography.baseFontSize,
			},
			iconHeight: Math.min(labelWidth, labelHeight) / 3,
		},
	};
}

const LabelDummy: React.FC = () => <div />;

const LabelPreview: React.FC<LabelPreviewProps> = (props: LabelPreviewProps) => {
	const withIconSection = props.withIcon || props.withQr;

	return (
		<div
			className="label"
			style={{
				padding: `${props.label.paddings.top}px ${props.label.paddings.right}px ${props.label.paddings.bottom}px ${props.label.paddings.left}px`,
			}}
		>
			<div
				className="label__content"
			>
				{
					withIconSection
						? (
							<div
								className="label__content__section section__images"
								style={{ fontSize: props.label.iconHeight }}
							>
								{
									props.withIcon
										? (
											<div className="section__images__logo">
												<img
													src={Logo}
													alt="logo"
												/>
											</div>
										) : null
								}
								{
									props.withQr
										? (
											<div className="section__images__qr">
												<QrcodeOutlined width="100%" height="100%" />
											</div>
										) : null
								}
							</div>
						) : null
				}
				<div className="label__content__section section__title">
					<div className="section__title__pet" style={{ fontSize: `${props.label.font.title}px` }}>
						{props.item.pet}
					</div>
					<div className="section__title__owner" style={{ fontSize: `${props.label.font.subtitle}px` }}>
						{props.item.owner}
					</div>
				</div>
				<div
					className="label__content__section section__info"
					style={{ fontSize: `${props.label.font.text}px` }}
				>
					<div className="section__info__text">
						{props.item.clinic}
					</div>
					<div className="section__info__text">
						{props.item.phone}
					</div>
				</div>
			</div>
		</div>
	);
};

const warning: string = `Before printing - make sure that printer settings correspond to Label Paper settings:
1. Paper sizes are the same 
2. Printing margins are set to 0
`;

export const PaperPreview: React.FC<PaperPreviewProps> = (props: PaperPreviewProps) => {
	const tracker = useTrackSize();
	const measurements = getMeasurements(props.paper, tracker.ref.current);

	const offset = props.offset ?? 0;
	const count = props.count ?? 1;
	const maxCount = props.paper.rows * props.paper.columns;

	const data = React.useMemo(() => {
		const list = props.data;

		if (!list.length) return [];
		if (count <= 0) return [];

		return list.flatMap((item: LabelData) => Array(count).fill(item)).slice(0, maxCount - offset);
	}, [props.data, count, maxCount, offset]);

	return (
		<>
			<Alert
				message={warning}
				type="info"
				closeText="Got it"
				style={{
					whiteSpace: 'pre-wrap',
				}}
			/>
			<div
				className="label-paper-container"
			>
				<div
					className="label-paper"
					style={{
						aspectRatio: `${props.paper.width} / ${props.paper.height}`,
						paddingTop: measurements.margins.top,
						paddingRight: measurements.margins.right,
						paddingBottom: measurements.margins.bottom,
						paddingLeft: measurements.margins.left,
					}}
					ref={tracker.ref}
				>
					{
						tracker.ref.current
							? (
								<div
									className="label-paper__content"
									style={{
										gridTemplateColumns: `repeat(${props.paper.columns}, 1fr)`,
										gridTemplateRows: `repeat(${props.paper.rows}, 1fr)`,
										gap: `${measurements.spacings.row}px ${measurements.spacings.column}px`,
									}}
								>
									{
										Array(offset)
											.fill(0)
											.map((_, i: number) => (
												// eslint-disable-next-line react/no-array-index-key
												<LabelDummy key={i} />
											))
									}
									{
										data.map((item: LabelData, index) => {
											const key = `${item.id}-${index}`;

											return (
												<LabelPreview
													key={key}

													label={measurements.label}
													item={item}

													withQr={props.printQr === undefined ? props.paper.printQr : props.printQr}
													withIcon={props.printIcon === undefined ? props.paper.printIcon : props.printIcon}
												/>
											);
										})
									}
								</div>
							) : null
					}
				</div>
			</div>
			<Legend defaultOpen>
				<Space
					direction="vertical"
					size="small"
				>
					<Space
						direction="vertical"
						size={0}
					>
						{LegendFields.paper.padding}
						{LegendFields.paper.spacing}
					</Space>

					<Space
						direction="vertical"
						size={0}
					>
						{LegendFields.label.padding}
						{LegendFields.label.images}
						{LegendFields.label.texts}
					</Space>

					<Space
						direction="vertical"
						size={0}
					>
						{LegendFields.paper.info}
						{LegendFields.label.info}
					</Space>
				</Space>
			</Legend>
		</>
	);
};
